// Export Workflow chat transcript + verbatim opening message on a saved ticket (Bug 9, 2026-10-07).
//
// The saved ticket used to keep only question/answer pairs (`followUps`) and `classificationValue`,
// so resuming it ("Continue" on Saved Work) rebuilt a lossy chat, and the opening message could be
// clobbered by a command label ("Save Work"). Two values now ride inside the ticket's
// `workflowData[0]` snapshot (a Mixed field — the Case schema has no `initialRequest` / transcript
// field, so top-level extras are silently dropped by Mongoose):
//   `_initialRequest`      the user's verbatim opening message;
//   `_chatTranscriptJson`  the chat as it was on screen (string-text messages only, result-card
//                          payloads stripped — they are re-filled from the saved workflow data).

export const EXPORT_INITIAL_REQUEST_KEY = '_initialRequest';
export const EXPORT_CHAT_TRANSCRIPT_KEY = '_chatTranscriptJson';

// Keeps the ticket well under Mongo's 16 MB document cap (the workflow snapshot is already MBs).
export const EXPORT_CHAT_TRANSCRIPT_MAX_CHARS = 1_500_000;

// Chat command labels that were stored as the "description" by older builds (Bug 9).
const COMMAND_LABELS = new Set(
  ['save work', 'save workflow', 'view saved', 'new session', 'end workflow', 'continue workflow'],
);

// Result cards render from the saved workflow data, so their (large) payloads aren't stored.
const RESULT_CARD_TYPES = new Set(['PHASE_RESULT', 'FINAL_RESULT']);

export interface ExportTranscriptMessage {
  id: number;
  sender: 'user' | 'bot';
  text: unknown;
  type?: string;
  payload?: any;
  thoughtSteps?: unknown[];
}

// A saved Export ticket (`cur_case.data` / route-state `resumeExportTicket`), loosely typed.
export type SavedExportTicketLike = { [key: string]: any } | null | undefined;

const norm = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

export const isExportCommandLabel = (value: unknown): boolean =>
  COMMAND_LABELS.has(norm(value).toLowerCase());

/** A usable opening message: non-empty and not a chat command label. */
const openingOrEmpty = (value: unknown) => {
  const text = norm(value);
  return text && !isExportCommandLabel(text) ? text : '';
};

const savedWorkflowSnapshot = (ticket: SavedExportTicketLike): { [key: string]: any } => {
  const workflowData = ticket?.workflowData;
  const raw = Array.isArray(workflowData) ? workflowData[0] : workflowData;
  return raw && typeof raw === 'object' ? raw : {};
};

/** The first `product_desc` the server echoed in the saved stream frames (old tickets). */
const openingFromAiStream = (aiStream: unknown) => {
  if (!Array.isArray(aiStream)) return '';
  for (const chunk of aiStream) {
    try {
      const frame = typeof chunk === 'string' ? JSON.parse(chunk) : chunk;
      const text = openingOrEmpty(frame?.product_desc);
      if (text) return text;
    } catch (error) {
      // skip a malformed frame
    }
  }
  return '';
};

/**
 * The user's verbatim opening message on a saved Export ticket: the snapshot's `_initialRequest`,
 * then a top-level `initialRequest`, then (tickets saved before this fix) the server's echoed
 * `product_desc` in the saved stream, then `classificationValue` unless it is a command label.
 */
export const savedExportOpeningMessage = (ticket: SavedExportTicketLike): string =>
  openingOrEmpty(savedWorkflowSnapshot(ticket)?.[EXPORT_INITIAL_REQUEST_KEY]) ||
  openingOrEmpty(ticket?.initialRequest) ||
  openingFromAiStream(ticket?.aiStream) ||
  openingOrEmpty(ticket?.classificationValue);

/**
 * What Saved Work shows as an Export ticket's "Classification Description": the verbatim opening
 * message, else the saved product description / commodity name — never a raw classificationValue
 * (an old ticket can hold a command label like "Save Work" there). '' when nothing usable exists.
 */
export const savedExportDescription = (ticket: SavedExportTicketLike): string =>
  savedExportOpeningMessage(ticket) ||
  openingOrEmpty(savedWorkflowSnapshot(ticket)?.product?.description) ||
  openingOrEmpty(ticket?.commodity?.commodityName);

const stripMessage = (message: ExportTranscriptMessage, keepThoughts: boolean) => {
  const next: ExportTranscriptMessage = { ...message };
  if (message.type && RESULT_CARD_TYPES.has(message.type)) {
    next.payload = {};
  }
  if (!keepThoughts) {
    delete next.thoughtSteps;
  }
  return next;
};

/**
 * The chat to store on the ticket, as JSON ('' when there is nothing worth storing). Keeps the
 * messages exactly as shown (text, cards with their clicked option, thinking panels), minus
 * non-string (JSX) text and the transient "Thinking..." placeholder. Drops the thinking panels,
 * then gives up, if the transcript would be too large.
 */
export const serializeExportChatTranscript = (messages: ExportTranscriptMessage[]): string => {
  const kept = (Array.isArray(messages) ? messages : []).filter(
    (message) => typeof message?.text === 'string' && message.text !== 'Thinking...',
  );
  if (!kept.some((message) => message.sender === 'user')) return '';
  for (const keepThoughts of [true, false]) {
    try {
      const json = JSON.stringify({
        v: 1,
        messages: kept.map((message) => stripMessage(message, keepThoughts)),
      });
      if (json.length <= EXPORT_CHAT_TRANSCRIPT_MAX_CHARS) return json;
    } catch (error) {
      return '';
    }
  }
  return '';
};

/** The stored chat of a saved Export ticket ([] when absent or unreadable). */
export const savedExportChatTranscript = (
  ticket: SavedExportTicketLike,
): ExportTranscriptMessage[] => {
  const raw = savedWorkflowSnapshot(ticket)?.[EXPORT_CHAT_TRANSCRIPT_KEY];
  if (typeof raw !== 'string' || !raw.trim()) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== 1 || !Array.isArray(parsed.messages)) return [];
    const messages = parsed.messages.filter(
      (message: any) =>
        message &&
        typeof message.id === 'number' &&
        (message.sender === 'user' || message.sender === 'bot') &&
        typeof message.text === 'string',
    );
    return messages.some((message: any) => message.sender === 'user') ? messages : [];
  } catch (error) {
    return [];
  }
};
